import { t } from '@lingui/core/macro';
import { Person } from '@mui/icons-material';
import { Box, Button, type ButtonProps, Tooltip } from '@mui/material';
import { type LinkOptions, useNavigate } from '@tanstack/react-router';
import { queryClient } from 'api';
import { faksApiInstance } from 'api/api';
import { useStartImpersonating } from 'hooks/useStartImpersonating';
import type { FC } from 'react';
import type {
  GetImpersonationTargetParams,
  GetImpersonationTargetResponse,
} from 'types/api/admin/roles';
import type { ResourceType, RoleKind } from 'types/models';
import { hasNoWebAccess } from 'utils/rolesUtils';

// The role to impersonate is resolved lazily on click, either from a known
// role id (role tables) or from a resource (owner role of a trade/pharmacy/lab).
type ResolvableRole =
  | { roleId: number; userId: number; resourceType?: never; resourceId?: never }
  | { resourceType: ResourceType; resourceId: number; roleId?: never; userId?: never };

type Props = Omit<ButtonProps, 'children'> &
  ResolvableRole &
  Partial<LinkOptions> & {
    // Passed by call sites that already know the role kind so the sales-rep
    // gate renders synchronously without a fetch.
    kind?: RoleKind;
    // Called once impersonation started, before navigation — lets a host (e.g. the
    // global search palette) tear down its own UI.
    onImpersonated?: VoidFunction;
  };

const fetchImpersonationTarget = (params: GetImpersonationTargetParams) =>
  queryClient.fetchQuery({
    queryKey: ['admin/roles/impersonation_target', params],
    queryFn: () =>
      faksApiInstance.get<GetImpersonationTargetResponse>('admin/roles/impersonation_target', {
        params,
      }),
  });

export const ImpersonateButton: FC<Props> = (props) => {
  const {
    roleId,
    userId,
    resourceType,
    resourceId,
    kind,
    to,
    params,
    search,
    onImpersonated,
    ...buttonProps
  } = props;
  const navigate = useNavigate();
  const startImpersonating = useStartImpersonating();

  const canImpersonate = !hasNoWebAccess(kind);

  const fetchRole = async () => {
    if (roleId) return { id: roleId, user_id: userId };
    if (resourceId) {
      const targetParams: GetImpersonationTargetParams = {
        resource_type: props.resourceType,
        resource_id: props.resourceId,
        kind,
      };
      const { role } = await fetchImpersonationTarget(targetParams);
      return role;
    }
    return undefined;
  };

  const onImpersonate = async () => {
    const role = await fetchRole();
    if (!role) return;

    startImpersonating(role);
    onImpersonated?.();
    navigate({ to: to ?? '/', params, search });
  };

  return (
    <Tooltip
      title={!canImpersonate ? t`L’impersonnification n’est pas disponible avec ce role` : ''}
      placement="top"
    >
      <Box width="fit-content">
        <Button
          startIcon={<Person />}
          onClick={onImpersonate}
          variant="contained"
          disabled={!canImpersonate}
          {...buttonProps}
        >
          {t`Impersonnifier`}
        </Button>
      </Box>
    </Tooltip>
  );
};
